import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {InvoiceDocument} from "../../../../../shared/interface/invoice-document";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../../shared/interface/user-permissions";
import {InvoiceDocumentService} from "../../../../../shared/services/invoice-document.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../../../shared/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FilterService} from "../../../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../../shared/ngrx/reducers";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../../shared/interface/response/counted-and-paginated";
import {Invoice} from "../../../../../shared/interface/invoice";
import {getCurrentRouteState} from "../../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../../shared/ngrx/route-serializer";
import {Uuid} from "../../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {Sort} from "@angular/material/sort";

@Component({
  selector: 'app-invoice-document-list',
  templateUrl: './invoice-document-list.component.html',
  styleUrls: ['./invoice-document-list.component.scss']
})
export class InvoiceDocumentListComponent implements OnDestroy, AfterViewInit {
  @Input() displayedColumns: string[];
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  items: InvoiceDocument[] = [];
  totalData = 0;

  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'number';
  sortOrder = 'desc';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private invoiceDocumentService: InvoiceDocumentService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.invoiceDocumentService.invoiceDocumentListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<InvoiceDocument>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }


  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$number: 'string',
      partner: 'string',
      gte$net: 'string',
      lte$net: 'string',
      company: 'int',
      contractType: 'int',
      gte$issueDate: 'string',
      lte$issueDate: 'string',
      withoutContract: 'int',
      recipientIdentifierType: 'string',
      recipientIdentifier: 'string',
      paymentStatus: 'string',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.displayedColumns = this.displayedColumns ?? data.displayedColumns ?? [
          'number', 'party', 'company', 'issueDate',
          'net', 'invoiceNumber', 'remainingAmount', 'deductedAmount', 'action'
        ];

        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.sortBy = state.params?.sortBy ? state.params.sortBy : 'number';
            this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
            this.loadListData();
          })
        ;
      });
  }

  filter(): void {
    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator?.pageSize || 10,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value,
      })
    ]);
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  loadListData(): void {
    this.invoiceDocumentService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  downloadReport(): void {
    this.invoiceDocumentService.downloadReport(this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();

    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      {
        page: 0,
        pageSize: this.paginator?.pageSize || 10,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }
    ]);
  }


  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      sortBy: $event.active,
      sortOrder: $event.direction,
      page: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value
    })]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


}
